/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { ProxmoxGuests } from "./ProxmoxGuests";
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon, ServerIcon } from "@heroicons/react/24/outline";
import { getOverview } from "@/api/overview";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, SkeletonRows } from "@/components/common/States";
import {
  CreateAgentRequest,
  MonitorAgent,
  UpdateAgentRequest,
  createMonitorAgent,
  deleteMonitorAgent,
  getMonitorAgents,
  updateMonitorAgent,
} from "@/api/monitor";
import { MONITOR_REFRESH_INTERVALS } from "@/constants/monitorRefreshIntervals";
import { useFocus, useReportSelected } from "@/utils/sidebarFocus";
import { showToast } from "@/components/common/Toast";
import { DeleteConfirmationDialog } from "@/components/common/DeleteConfirmationDialog";
import { MonitorAgentForm } from "@/components/monitor/MonitorAgentForm";
import { useMobileHeaderAction } from "@/context/tabNavigation";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { ServerCard } from "./ServerCard";
import { ServerTable } from "./ServerTable";
import { ServerDetailView } from "./ServerDetailView";

const SELECTED_KEY = "nyrvex-servers-selected";
const FEATURED_KEY = "nyrvex-featured-monitor-agents";

// The Home tab's featured widgets keep their own list of agent ids; when a
// server is removed or disabled it has to drop out of that list too.
function dropFromFeatured(id: number) {
  try {
    const parsed = JSON.parse(localStorage.getItem(FEATURED_KEY) || "[]");
    const ids: number[] = Array.isArray(parsed) ? parsed : [];
    const next = ids.filter((x) => x !== id);
    if (next.length !== ids.length) {
      localStorage.setItem(FEATURED_KEY, JSON.stringify(next));
      window.dispatchEvent(new Event("storage"));
      window.dispatchEvent(new Event("featured-agents-changed"));
    }
  } catch {
    /* localStorage unavailable */
  }
}

export const ServersTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    try {
      const v = localStorage.getItem(SELECTED_KEY);
      return v ? parseInt(v, 10) : null;
    } catch {
      return null;
    }
  });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<MonitorAgent | null>(null);
  const [agentToDelete, setAgentToDelete] = useState<MonitorAgent | null>(null);

  const { data: agents = [], isLoading } = useQuery({
    queryKey: ["monitor-agents"],
    queryFn: getMonitorAgents,
    refetchInterval: MONITOR_REFRESH_INTERVALS.AGENTS_LIST,
  });

  // Sessions can hand us a server to open (e.g. from the Home tab).
  useEffect(() => {
    try {
      const pre = sessionStorage.getItem("nyrvex-preselect-agent");
      if (pre && agents.some((a) => a.id === parseInt(pre, 10))) {
        setSelectedId(parseInt(pre, 10));
        sessionStorage.removeItem("nyrvex-preselect-agent");
      }
    } catch {
      /* ignore */
    }
  }, [agents]);

  // Sidebar sub-item clicks select a specific server.
  useFocus("monitor", (id) => setSelectedId(parseInt(id, 10)));

  // Keep the selection valid: fall back to the first server.
  const { data: overview } = useQuery({ queryKey: ["overview"], queryFn: getOverview, staleTime: 30_000 });
  const downNames = new Set((overview?.problems ?? []).filter((p) => p.type === "agent").map((p) => p.name));
  const selected =
    agents.find((a) => a.id === selectedId) ??
    agents.find((a) => a.enabled && !downNames.has(a.name)) ??
    agents[0] ??
    null;
  useReportSelected("monitor", selected?.id);
  const detailRef = useRef<HTMLDivElement>(null);
  const pickServer = (id: number) => {
    setSelectedId(id);
    // the list can be long; bring the opened server's details into view
    requestAnimationFrame(() => detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  useEffect(() => {
    if (!selected) return;
    try {
      localStorage.setItem(SELECTED_KEY, String(selected.id));
    } catch {
      /* ignore */
    }
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const createMutation = useMutation({
    mutationFn: createMonitorAgent,
    onSuccess: (agent) => {
      queryClient.invalidateQueries({ queryKey: ["monitor-agents"] });
      setIsFormOpen(false);
      setSelectedId(agent.id);
      showToast("Server added", "success", { description: `${agent.name} has been added` });
    },
    onError: (e: Error) => showToast("Failed to add server", "error", { description: e.message }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAgentRequest }) => updateMonitorAgent(id, data),
    onSuccess: (_, vars) => {
      if (!vars.data.enabled) dropFromFeatured(vars.id);
      queryClient.invalidateQueries({ queryKey: ["monitor-agents"] });
      setIsFormOpen(false);
      setEditingAgent(null);
      showToast("Server updated", "success");
    },
    onError: (e: Error) => showToast("Failed to update server", "error", { description: e.message }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteMonitorAgent,
    onSuccess: (_, id) => {
      dropFromFeatured(id);
      queryClient.invalidateQueries({ queryKey: ["monitor-agents"] });
      showToast("Server removed", "success", { description: agentToDelete ? `${agentToDelete.name} has been removed` : undefined });
      setAgentToDelete(null);
    },
    onError: (e: Error) => showToast("Failed to remove server", "error", { description: e.message }),
  });

  const handleSubmit = (data: CreateAgentRequest) => {
    if (editingAgent) updateMutation.mutate({ id: editingAgent.id, data: { ...data, id: editingAgent.id } });
    else createMutation.mutate(data);
  };

  const openAdd = () => {
    setEditingAgent(null);
    setIsFormOpen(true);
  };

  const desktop = useIsDesktop();
  const [view, setView] = useState<"table" | "cards">(() => {
    try {
      return localStorage.getItem("nyrvex-servers-view") === "cards" ? "cards" : "table";
    } catch {
      return "table";
    }
  });
  const changeView = (v: "table" | "cards") => {
    setView(v);
    try {
      localStorage.setItem("nyrvex-servers-view", v);
    } catch {
      /* ignore */
    }
  };

  // Mobile: the "Servers" title already shows in the top bar; surface the add
  // action there and drop the duplicate toolbar below.
  useMobileHeaderAction(
    () => (
      <button
        onClick={openAdd}
        aria-label="Add server"
        className="flex h-8 w-8 items-center justify-center text-gray-600 dark:text-gray-400"
      >
        <PlusIcon className="h-5 w-5" />
      </button>
    ),
    []
  );

  return (
    <div className="gh sv">
      <PageHeader
        title="Servers"
        subtitle={agents.length === 1 ? "1 server" : `${agents.length} servers`}
        actions={
          <>
            <div className="cc-seg" role="tablist" aria-label="View">
              {(["table", "cards"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  role="tab"
                  aria-selected={view === v}
                  className={`cc-seg-btn${view === v ? " cc-seg-btn--on" : ""}`}
                  onClick={() => changeView(v)}
                >
                  {v === "table" ? "Table" : "Cards"}
                </button>
              ))}
            </div>
            <button className="gh-btn gh-btn--go" onClick={openAdd}>
              <PlusIcon /> Add server
            </button>
          </>
        }
      />

      <ProxmoxGuests />

      {isLoading ? (
        <SkeletonRows rows={8} />
      ) : agents.length === 0 ? (
        <EmptyState
          icon={<ServerIcon className="h-8 w-8" />}
          title="No servers yet"
          hint="Install the agent on a machine, then add it here to see live CPU, memory, disks, network and health."
          action={
            <button className="gh-btn gh-btn--go" onClick={openAdd}>
              <PlusIcon /> Add server
            </button>
          }
        />
      ) : (
        <>
          {view === "table" && desktop ? (
            <ServerTable agents={agents} selectedId={selected?.id} onSelect={pickServer} />
          ) : (
            <div className="sv-fleet">
              {agents.map((a) => (
                <ServerCard key={a.id} agent={a} selected={selected?.id === a.id} onSelect={() => pickServer(a.id)} />
              ))}
            </div>
          )}
          {selected && (
            <div ref={detailRef} className="sv-detail-anchor">
            <ServerDetailView
              key={selected.id}
              agent={selected}
              onEdit={() => {
                setEditingAgent(selected);
                setIsFormOpen(true);
              }}
              onDelete={() => setAgentToDelete(selected)}
            />
            </div>
          )}
        </>
      )}

      <MonitorAgentForm
        agent={editingAgent}
        onSubmit={handleSubmit}
        onCancel={() => {
          setIsFormOpen(false);
          setEditingAgent(null);
        }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        isOpen={isFormOpen}
      />

      <DeleteConfirmationDialog
        isOpen={agentToDelete !== null}
        onClose={() => setAgentToDelete(null)}
        onConfirm={() => agentToDelete && deleteMutation.mutate(agentToDelete.id)}
        itemName={agentToDelete?.name || ""}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
};
